import { useMemo } from 'react';

import { useTranslations } from '@heidi/runtime/i18n';
import type { OnboardingV2DocumentField } from '../../onboardingV2AssetsTypes';
import {
  parseExampleNoteIntoSections,
  type EnhancedLine,
} from '../../utils/parseExampleNoteIntoSections';

export interface LearnBasicsDocumentOption {
  /** Analytics slug passed to `trackDocumentCreated`. */
  slug: string;
  /** Display name (tab label + card title). */
  name: string;
  /** Short helper shown under the card title. */
  helper: string;
  /**
   * Parsed document body for the preview, or `null` to fall back to the primary
   * per-specialty API document body (used only while assets are still loading).
   */
  sections: EnhancedLine[][] | null;
}

/** Stable analytics slug derived from the document type label. */
function typeToSlug(type: string, index: number): string {
  const slug = type
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return slug || `document_${index + 1}`;
}

/**
 * The three per-specialty recommended documents offered on the Learn-the-basics
 * Document step, built directly from the API `documents` array (type + body).
 * Shared by the left-rail picker and the preview so names / bodies / analytics
 * slugs stay in sync. Each option's `sections` is its parsed real body.
 *
 * While assets are still loading (`documents` undefined/empty) a single primary
 * placeholder is returned whose `sections: null` falls back to the preview's
 * primary `documentSections`.
 */
export function useLearnBasicsDocumentOptions(
  documents?: OnboardingV2DocumentField[]
): LearnBasicsDocumentOption[] {
  const { t } = useTranslations();

  return useMemo<LearnBasicsDocumentOption[]>(() => {
    const helper = t('signup.learnBasicsDocumentHelper');
    const fallbackDocumentType = t('common.document');

    if (!documents || documents.length === 0) {
      return [
        {
          slug: 'specialty_document',
          name: fallbackDocumentType,
          helper,
          sections: null,
        },
      ];
    }

    return documents.map((doc, index) => ({
      slug: index === 0 ? 'specialty_document' : typeToSlug(doc.type, index),
      name: doc.type || fallbackDocumentType,
      helper,
      sections: parseExampleNoteIntoSections(doc.body),
    }));
  }, [t, documents]);
}
